// useForcedAuthEpochLogout.js
import { useCallback, useEffect } from 'react';

// Context
import { useUI } from 'context/UIContext';

// Firebase
import { signOut } from 'firebase/auth';
import { auth } from 'firebaseConfig';

// Migration
import {
  AUTH_SESSION_EPOCH,
  AUTH_SESSION_EPOCH_STORAGE_KEY,
} from 'root/migrationAuthEpoch';

const forceLogoutIfEpochMismatch = async (setLoginOpen) => {
  if (typeof window === 'undefined') return;

  const storedEpoch = window.localStorage.getItem(AUTH_SESSION_EPOCH_STORAGE_KEY);
  if (storedEpoch === AUTH_SESSION_EPOCH) return;

  if (auth.currentUser) {
    try {
      // Force a refresh so server-side token revocation takes effect immediately.
      await auth.currentUser.getIdToken(true);
    } catch (_error) {
      // Expected after refresh tokens are revoked.
    }

    if (auth.currentUser) {
      await signOut(auth);
    }
  }

  window.localStorage.setItem(AUTH_SESSION_EPOCH_STORAGE_KEY, AUTH_SESSION_EPOCH);
  setLoginOpen(true);
};

/**
 * useForcedAuthEpochLogout
 *
 * Signs users out of dropspot.live after the orbital migration cutover.
 * Re-checks when the tab regains focus so stale weekend tabs cannot keep writing.
 */
const useForcedAuthEpochLogout = () => {
  const { setLoginOpen } = useUI();

  const enforceEpoch = useCallback(() => {
    void forceLogoutIfEpochMismatch(setLoginOpen);
  }, [setLoginOpen]);

  useEffect(() => {
    enforceEpoch();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        enforceEpoch();
      }
    };

    window.addEventListener('focus', enforceEpoch);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', enforceEpoch);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enforceEpoch]);
};

export default useForcedAuthEpochLogout;
